import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateInput } from 'semantic-ui-calendar-react';
import { Dropdown } from 'semantic-ui-react';
import { DownloadService } from '~/api/services/downloadFiles';
import i18n from '~/components/i18n';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import TableComponent from '~/components/TableComponent';
import { FTPLogsQuery } from '~/QueryHooks/FTPLogsQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import { FiltersWrapper } from './style';
import ExcelExport from '~/components/ExcelExport';

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Type'),
    key: 'typeLabel'
  },
  {
    name: i18n.t('Action'),
    key: 'slugLabel'
  },
  {
    name: i18n.t('Start'),
    key: 'start',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('End'),
    key: 'end',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Exported'),
    key: 'exported'
  },
  {
    name: i18n.t('Imported'),
    key: 'imported'
  },
  {
    name: i18n.t('Updated'),
    key: 'updated'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'verified'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Original file'),
    type: 'icon',
    name: 'file alternate',
    size: 'large',
    actionType: 'download original'
  },
  {
    popupMessage: i18n.t('Download log file'),
    type: 'icon',
    name: 'file alternate outline',
    size: 'large',
    actionType: 'download'
  }
]

const FTPLogs: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    dateFrom: '',
    dateTo: '',
    type: '',
    ftp: ''
  })

  const {
    isLoading: isBusyData,
    data: ftplogsData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FTPLogsQuery.getFtpLogsList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
    dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
  });

  const {
    isLoading: isBusyDataOptions,
    refetch: fetchOptions,
    data: ftplogsOptions
  } = FTPLogsQuery.getFtpLogsOptions();

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  useEffect(() => {
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  async function handleDownload(id, route, name, type) {
    setLoadingAction({
      type: type,
      id: id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFile(route, name, { ...dataFiltered })
    } catch (error) {
      if (error.response?.data) {
        notify(error.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'download original':
        return row?.originalFile ? handleDownload(row?.id, `ftp-logs/${row?.id}/get-file`, 'original_file', type) : notify(t('No original file'), 'info')
      case 'download':
        return handleDownload(row?.id, `ftp-logs/${row?.id}/get-log-file`, 'log_file', type)
    }
  }

  function renderFilters() {
    return <FiltersWrapper>
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        clearIcon={'close'}
        onKeyPress={handleKeyPress}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateFrom: value
        })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date to')}
        closable
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        onKeyPress={handleKeyPress}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        minDate={dataFiltered.dateFrom}
        value={dataFiltered.dateTo}
        iconPosition='left'
        onChange={(event, { name, value }) => {
          setDataFiltered({
            ...dataFiltered,
            dateTo: value
          })
        }}
      />
      <Dropdown
        selectOnBlur={false}
        selection
        clearable
        options={ftplogsOptions?.types.map(type => ({ value: type.key, text: type.label })) || []}
        value={dataFiltered.type}
        placeholder={t('Select type')}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, type: data.value as string })}
      />
      <Dropdown
        selectOnBlur={false}
        clearable
        selection
        placeholder={t('Select ftp')}
        options={ftplogsOptions?.ftps.map(ftp => ({ value: ftp.key, text: ftp.label })) || []}
        value={dataFiltered.ftp}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, ftp: data.value as string })}
      />
    </FiltersWrapper>
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      actions={getActions()}
      tableColumnHeaders={getTableColumnHeaders()}
      loadingAction={loadingAction}
      dataFiltered={dataFiltered}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }
  const { filtered, rows } = ftplogsData ? ftplogsData : { filtered: 0, rows: [] };

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderActions() {
    return <ExcelExport
      url={'ftp-logs/export'}
      fileName={'ftp_log'}
      data={{
        ...dataFiltered,
        dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
        dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
      }}
    />
  }

  return (
    <Layout
      title={t('Ftp logs')}
      isBusy={isBusyData || isBusyDataOptions}
      segmented
      actions={renderActions()}
      filters={renderFilters()}
    >
      {ftplogsData && ftplogsOptions && renderTable()}
    </Layout>
  );
};

export default FTPLogs;
